/**
 * Section displaying all completed steps from finished levels
 * Shows steps in a compact grid with original colors restored
 * Collapsed by default when all steps are complete (to highlight settings cards)
 */

import React from "react";
import { Box, Flex, Paper, Stack, Text, Title } from "@mantine/core";
import { IconCheck, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import type { OnboardingStepItem } from "../types";
import { getLevelConfig, type LevelType } from "../utils/levelUtils";
import {
  getStepConfig,
  getStepDescription,
  getStepTitle,
} from "../config/onboardingStepsConfig";

interface CompletedStepsSectionProps {
  completedSteps: OnboardingStepItem[];
  onStepClick: (stepId: string) => void;
  showHeader?: boolean;
  allStepsComplete?: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

/**
 * Displays all completed steps in a dedicated section
 * Collapsed by default when all steps are complete (to highlight settings cards)
 */
const CompletedStepsSection: React.FC<CompletedStepsSectionProps> = ({
  completedSteps,
  onStepClick,
  showHeader = true,
  allStepsComplete = false,
  isCollapsed,
  setIsCollapsed,
}) => {
  if (completedSteps.length === 0) {
    return null;
  }

  return (
    <Paper
      shadow={allStepsComplete ? undefined : "sm"}
      radius="lg"
      p="lg"
      style={{
        border: allStepsComplete ? "none" : "1px solid #51cf66",
        width: "fit-content",
        maxWidth: "90%",
        marginInline: "auto",
      }}
    >
      <Stack gap="md">
        {/* Header - always show, clickable to toggle collapse */}
        {showHeader && (
          <Flex
            align="center"
            justify="space-between"
            gap="xs"
            style={{ cursor: "pointer" }}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <Flex align="center" gap="xs">
              <Title order={4} c="#51cf66">
                Completed Steps
              </Title>
              <Text size="sm" c="dimmed">
                ({completedSteps.length})
              </Text>
            </Flex>
            {isCollapsed ? (
              <IconChevronDown size={20} color="#51cf66" />
            ) : (
              <IconChevronUp size={20} color="#51cf66" />
            )}
          </Flex>
        )}

        {/* Completed Steps Grid - only show when not collapsed */}
        {!isCollapsed &&
          (() => {
            // Group steps by level/category
            const basicSteps = completedSteps.filter(
              (step) => step.category === "basic"
            );
            const advancedSteps = completedSteps.filter(
              (step) => step.category === "advanced"
            );
            const eliteSteps = completedSteps.filter(
              (step) => step.category === "elite"
            );

            // Render a single step card
            const renderStepCard = (step: OnboardingStepItem) => {
              const config = getStepConfig(step.id);

              // Skip rendering if config doesn't exist for this step
              if (!config) {
                console.warn(`No config found for step: ${step.id}`);
                return null;
              }

              const levelConfig = getLevelConfig(step.category as LevelType);
              const IconComponent = config.icon;
              const title = getStepTitle(step.id, step.metadata);
              const description = getStepDescription(step.id, step.metadata);

              return (
                <Paper
                  key={step.id}
                  shadow="sm"
                  p="md"
                  radius="md"
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#ffffff",
                    border: `2px solid ${levelConfig.color}`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    maxWidth: "200px",
                    minWidth: "180px",
                    width: "100%",
                    aspectRatio: "1",
                    display: "flex",
                    flexDirection: "column",
                    opacity: 0.9,
                  }}
                  onClick={() => onStepClick(step.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.opacity = "0.9";
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.08)";
                  }}
                >
                  {/* Completed checkmark badge */}
                  <Box
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "#51cf66",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      zIndex: 1,
                    }}
                  >
                    <IconCheck size={14} color="white" stroke={3} />
                  </Box>

                  <Stack
                    gap="sm"
                    style={{ flex: 1, position: "relative", zIndex: 0 }}
                  >
                    {/* Icon */}
                    <Box
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "10px",
                        background: config.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComponent size={24} color="white" stroke={1.5} />
                    </Box>

                    {/* Title */}
                    <Text size="md" fw={600} c="dark" lineClamp={2}>
                      {title}
                    </Text>

                    {/* Description */}
                    <Text
                      size="xs"
                      c="dimmed"
                      lineClamp={3}
                      style={{ lineHeight: 1.5 }}
                    >
                      {description}
                    </Text>
                  </Stack>
                </Paper>
              );
            };

            // Render a level row with its steps
            const renderLevelRow = (
              levelSteps: OnboardingStepItem[],
              levelType: LevelType
            ) => {
              if (levelSteps.length === 0) return null;

              const levelConfig = getLevelConfig(levelType);

              return (
                <Stack gap="sm" key={levelType}>
                  {/* Level title */}
                  <Title
                    order={4}
                    c={levelConfig.color}
                    style={{
                      textAlign: "center",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {levelConfig.name}
                  </Title>
                  {/* Steps row */}
                  <Flex wrap="wrap" gap="md" justify="center">
                    {levelSteps.map(renderStepCard)}
                  </Flex>
                </Stack>
              );
            };

            return (
              <Stack gap="lg">
                {renderLevelRow(basicSteps, "basic")}
                {renderLevelRow(advancedSteps, "advanced")}
                {renderLevelRow(eliteSteps, "elite")}
              </Stack>
            );
          })()}
      </Stack>
    </Paper>
  );
};

export default CompletedStepsSection;
